/** Helpers extraits de InterventionsManage.tsx pour respecter la limite de 500 lignes par fichier. */

/** Valeurs `cta_intervention.status` côté API (voir mapStatutToDb sur le serveur). */
export const SCENARIO_STATUS_OPTIONS = [
  { value: 'en_creation', label: 'En création' },
  { value: 'en_test', label: 'En test' },
  { value: 'actif', label: 'Actif' },
] as const;

export type ScenarioStatusValue = (typeof SCENARIO_STATUS_OPTIONS)[number]['value'];

export interface InterventionHistoryEntry {
  id: number;
  action: 'created' | 'updated' | 'status_changed' | 'validated' | string;
  actor_username?: string | null;
  previous_status?: string | null;
  new_status?: string | null;
  details?: { changed_fields?: string[] } | null;
  created_at: string;
}

export function normalizeScenarioStatus(raw: string): ScenarioStatusValue {
  const u = (raw || '').trim().toLowerCase();
  if (u === 'actif' || u === 'active') return 'actif';
  if (u === 'en_test') return 'en_test';
  return 'en_creation';
}

export function scenarioStatusSelectRingClass(statusNorm: ScenarioStatusValue): string {
  if (statusNorm === 'actif') return 'border-emerald-600/60 ring-1 ring-emerald-500/25';
  if (statusNorm === 'en_test') return 'border-amber-600/60 ring-1 ring-amber-500/25';
  return 'border-gray-600 ring-1 ring-gray-600/30';
}

/** Bâtiment éligible pour /api/cta/generate-intervention (secteur 5 km + lié CTA). */
export async function pickSpawnBuildingId(
  token: string,
  apiUrl: string
): Promise<{ id: number; error?: string }> {
  const res = await fetch(`${apiUrl}/api/buildings`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  const data = await res.json();
  if (!data.success || !Array.isArray(data.data)) {
    return { id: 0, error: 'Impossible de charger vos bâtiments.' };
  }
  const eligible = data.data.filter(
    (b: { type?: string; actif?: boolean; cta_id?: unknown; localisation?: string }) =>
      b &&
      b.type !== 'cta' &&
      b.actif &&
      b.cta_id != null &&
      b.localisation &&
      String(b.localisation).trim() !== ''
  );
  if (eligible.length === 0) {
    return {
      id: 0,
      error:
        'Aucun site opérationnel actif, géolocalisé et lié à un CTA. En jeu : créez un site, liez-le au CTA, puis réessayez.',
    };
  }
  return { id: Number(eligible[0].id) };
}

export function isScenarioReferentClient(referentFunctions: string[]): boolean {
  return referentFunctions.some((f) => /scénariste|scenariste/i.test(f));
}

export function formatScenarioDateTime(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('fr-FR');
}

export function scenarioActionLabel(action: string): string {
  if (action === 'created') return 'Création';
  if (action === 'updated') return 'Modification';
  if (action === 'status_changed') return 'Changement de statut';
  if (action === 'validated') return 'Validation';
  return action;
}
